import cx from "classnames";
import {
  formatStatusLabel,
  getStatusVariant,
  TableCellStatusPill,
} from "components/TableCellStatusPill/TableCellStatusPill";
import Table from "components/Table/Table";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { format, parse } from "date-fns";
import { useEffect, useState } from "react";
import { setShowBackgroundJobs } from "reducers/drawer";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import type { BackgroundJobDataType } from "types/DataTypes";
import { DATE_AND_TIME_FORMAT, formatMinutes } from "utils/date-utils";
import { useDispatch, useSelector } from "utils/redux";

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) {
    return "-";
  }
  try {
    // try to parse the date in MM/dd/yyyy hh:mm a format first (API format)
    const parsedDate = parse(dateString, "MM/dd/yyyy hh:mm a", new Date());

    // check if the parsed date is valid
    if (isNaN(parsedDate.getTime())) {
      throw new Error("Invalid date");
    }

    return format(parsedDate, DATE_AND_TIME_FORMAT);
  } catch (error) {
    console.warn("Invalid date string:", dateString, error);
    return dateString; // fallback to original string
  }
};

type BackgroundJobTableDataType = {
  id: number;
  created: string;
  duration: string;
  job_type: string;
  status: string;
  status_message: string;
  started_by: string;
  last_status_update_at: string;
  file_id: string;
  rre_name: string;
  bulk_id: string;
};

/* background jobs drawer, currently used on reporting page */
export const BackgroundJobsDrawer = () => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);

  const {
    data: background_jobs = { results: [], count: 0 },
    isFetching,
    refetch,
  } = useGetBackgroundJobsQuery(
    {
      filters: {
        limit: PAGE_SIZE,
        offset: OFFSET(page),
        // filter for finance-related background jobs
        job_type: "BATCH_POST_INVOICES",
      },
    },
    {
      skip: false,
    },
  );

  const showBackgroundJobs = useSelector(
    (state) => state.drawer.showBackgroundJobs,
  );

  useEffect(() => {
    if (showBackgroundJobs) {
      refetch();
    }
  }, [showBackgroundJobs, refetch]);

  const rows: BackgroundJobTableDataType[] =
    background_jobs?.results?.map((job: BackgroundJobDataType) => {
      let durationMinutes: number | null = null;

      if (job.duration !== null) {
        durationMinutes = job.duration > 0 ? job.duration : null;
      } else if (job.minutes_in_progress > 0) {
        durationMinutes = job.minutes_in_progress;
      }

      const formattedDuration = durationMinutes
        ? formatMinutes(durationMinutes)
        : "-";

      return {
        id: job.id,
        created: job.created || "",
        duration: formattedDuration,
        job_type: job.job_type || "",
        status: job.status || "",
        status_message: job.status_message || "-",
        started_by: job.started_by || "",
        last_status_update_at: job.last_status_update_at
          ? job.last_status_update_at.toString()
          : "",
        file_id: job.file_id ? String(job.file_id) : "-",
        rre_name: job.rre_name ?? "-",
        bulk_id: job.bulk_id ?? "-",
      };
    }) || [];

  const columns = [
    {
      key: "id",
      name: "ID",
      ...commonColumnProps,
      width: "80px",
    },
    {
      key: "created",
      name: "Started",
      ...commonColumnProps,
      renderCell: ({ row }: { row: BackgroundJobTableDataType }) => (
        <div>{formatDate(row.created)}</div>
      ),
    },
    {
      key: "job_type",
      name: "Job Type",
      ...commonColumnProps,
      renderCell: ({ row }: { row: BackgroundJobTableDataType }) => (
        <div>
          {row.job_type === "BATCH_POST_INVOICES"
            ? "Batch Post Invoices"
            : row.job_type}
        </div>
      ),
    },
    {
      key: "status",
      name: "Status",
      ...commonColumnProps,
      renderCell: ({ row }: { row: BackgroundJobTableDataType }) => (
        <TableCellStatusPill
          label={formatStatusLabel(row.status)}
          variant={getStatusVariant(row.status)}
        />
      ),
    },
    {
      key: "duration",
      name: "Duration",
      ...commonColumnProps,
    },
    {
      key: "status_message",
      name: "Message",
      ...commonColumnProps,
    },
    {
      key: "started_by",
      name: "Started By",
      ...commonColumnProps,
    },
    {
      key: "file_id",
      name: "File No",
      ...commonColumnProps,
    },
    {
      key: "rre_name",
      name: "RRE",
      ...commonColumnProps,
    },
    {
      key: "bulk_id",
      name: "Bulk ID",
      ...commonColumnProps,
    },
  ];

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showBackgroundJobs,
      })}
    >
      <div className="documents-wrapper w-search">
        <div className="header-primary flex jc-sb">
          Background Jobs
          <a
            className="header-primary alt-btn"
            onClick={() => dispatch(setShowBackgroundJobs(false))}
          >
            Close
          </a>
        </div>

        <div className="data-values mrg-top">
          <Table
            className="background-jobs-table"
            columns={columns}
            rows={rows}
            rowKeyGetter={(row: BackgroundJobTableDataType) => row.id}
            onCellClick={() => {}}
            onSelectedRowsChange={() => {}}
            drawer={true}
            emptyTableMessage="No background jobs found"
            pagination={{
              pageSize: PAGE_SIZE,
              pageIndex: page,
              rowCount: background_jobs.count,
              onPageChange: (pageIndex) => setPage(pageIndex),
              isLoading: isFetching,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default BackgroundJobsDrawer;
